import { useState, useEffect } from 'react';

export const isMobileUserAgent = (): boolean => {
    if (typeof navigator === 'undefined') return false;

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );
};

export const isTouchDevice = (): boolean => {
    if (typeof window === 'undefined') return false;

    return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore
        navigator.msMaxTouchPoints > 0
    );
};

export const isMobileScreenSize = (): boolean => {
    if (typeof window === 'undefined') return false;

    return window.innerWidth <= 768; // md breakpoint
};

export const isMobileDevice = (): boolean => {
    return isMobileUserAgent() || (isTouchDevice() && isMobileScreenSize());
};

export const useMobileDetection = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [isTouch, setIsTouch] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    useEffect(() => {
        const updateMobileStatus = () => {
            setIsMobile(isMobileDevice());
            setIsTouch(isTouchDevice());
            setIsSmallScreen(isMobileScreenSize());
        };

        updateMobileStatus();

        window.addEventListener('resize', updateMobileStatus);
        window.addEventListener('orientationchange', updateMobileStatus);

        return () => {
            window.removeEventListener('resize', updateMobileStatus);
            window.removeEventListener('orientationchange', updateMobileStatus);
        };
    }, []);

    return {
        isMobile,
        isTouch,
        isSmallScreen,
        isMobileUserAgent: isMobileUserAgent(),
        isDesktop: !isMobile,
        isMobileOrTablet: isMobile || isTouch,
    };
};

export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
    if (typeof window === 'undefined') return 'desktop';

    const width = window.innerWidth;
    const userAgent = navigator.userAgent;

    if (width < 768 && isMobileUserAgent()) {
        return 'mobile';
    }
    if ((width >= 768 && width < 1024) || /iPad|Android(?!.*Mobile)/i.test(userAgent)) {
        return 'tablet';
    }
    return 'desktop';
};

export const useDeviceType = () => {
    const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

    useEffect(() => {
        const updateDeviceType = () => {
            setDeviceType(getDeviceType());
        };

        updateDeviceType();

        window.addEventListener('resize', updateDeviceType);
        window.addEventListener('orientationchange', updateDeviceType);

        return () => {
            window.removeEventListener('resize', updateDeviceType);
            window.removeEventListener('orientationchange', updateDeviceType);
        };
    }, []);

    return {
        deviceType,
        isMobile: deviceType === 'mobile',
        isTablet: deviceType === 'tablet',
        isDesktop: deviceType === 'desktop',
    };
};